import type { InteractionEvent } from '../../interaction/types';
import type { CarmViewService } from '../../services/carm-view-service';
import type { IViewport } from '../../viewports/types';
import type { ITool } from '../types';
import { FrameCoalescer } from './frame-coalescer';

/** Rotation factor (degrees per normalized viewport unit), same feel as RotateTool. */
const ROTATION_FACTOR = 360;

/** Degrees of oblique rotation per unit of wheel deltaY, same feel as VerticalRotateTool. */
const WHEEL_OBLIQUE_SENSITIVITY = 0.02;

/**
 * Constrained C-arm rotation tool — active only during C-arm edit mode.
 *
 * Instead of orbiting the camera freely, drags and wheel ticks accumulate
 * deltas onto the draft angles of CarmViewService:
 *   - horizontal drag → oblique (LAO/RAO)
 *   - vertical drag → craniocaudal (CRA/CAU)
 *   - wheel → oblique (LAO/RAO)
 *
 * The service clamps into the C-arm envelope and CarmCameraBinding recomputes
 * the camera pose from the angles, so the camera can never leave a valid
 * C-arm pose — dragging past a limit just pins at the boundary.
 *
 * Signs are chosen to match the free RotateTool feel at the frontal view.
 * No-op (events not consumed) when no draft edit is active.
 */
export class CarmRotateTool implements ITool {
    static readonly toolName = 'carm-rotation-tool';

    readonly id = CarmRotateTool.toolName;
    readonly name = 'C-arm Rotate';
    readonly cursor = 'grab';
    readonly completed = false;

    private pendingDx = 0;
    private pendingDy = 0;
    private pendingWheelDeltaY = 0;
    private pendingViewport: IViewport | null = null;
    private coalescer = new FrameCoalescer(() => this.applyPending());

    constructor(private readonly carmViewService: CarmViewService) {}

    activate(_viewport: IViewport): void {}

    deactivate(): void {
        this.coalescer.cancel();
        this.resetPending();
    }

    cancel(): void {
        this.coalescer.cancel();
        this.resetPending();
    }

    handleEvent(event: InteractionEvent): boolean {
        if (!this.carmViewService.getDraft()) return false;

        if (event.type === 'mouse_drag_end') {
            this.coalescer.flush();
            return true;
        }

        if (event.type === 'wheel') {
            if (event.deltaY === undefined) return false;
            this.pendingWheelDeltaY += event.deltaY;
            this.coalescer.schedule();
            return true;
        }

        if (event.type !== 'mouse_drag') return false;

        const [dx, dy] = event.deltaPoints.canvas;
        this.pendingDx += dx;
        this.pendingDy += dy;
        this.pendingViewport = event.viewport;
        this.coalescer.schedule();
        return true;
    }

    private applyPending(): void {
        const vp = this.pendingViewport;
        const dx = this.pendingDx;
        const dy = this.pendingDy;
        const wheelDeltaY = this.pendingWheelDeltaY;
        this.resetPending();

        if (vp && (dx !== 0 || dy !== 0)) {
            this.applyAngleDeltas(vp, dx, dy);
        }
        if (wheelDeltaY !== 0) {
            this.applyWheelOblique(wheelDeltaY);
        }
    }

    private resetPending(): void {
        this.pendingDx = 0;
        this.pendingDy = 0;
        this.pendingWheelDeltaY = 0;
        this.pendingViewport = null;
    }

    private applyWheelOblique(deltaY: number): void {
        const draft = this.carmViewService.getDraft();
        if (!draft) return;

        // The service clamps; the camera binding reapplies the pose
        this.carmViewService.setDraftAngles({
            craniocaudal: draft.angles.craniocaudal,
            oblique: draft.angles.oblique + deltaY * WHEEL_OBLIQUE_SENSITIVITY,
        });
    }

    private applyAngleDeltas(viewport: IViewport, dx: number, dy: number): void {
        const draft = this.carmViewService.getDraft();
        if (!draft) return;

        const canvas = viewport.getCanvas();
        if (!canvas) return;

        const { width, height } = canvas.getBoundingClientRect();
        if (width === 0 || height === 0) return;

        // Same zoom compensation as RotateTool: slow down when zoomed in
        const DEFAULT_VIEW_ANGLE = 30;
        const camera = viewport.getRenderer(0).getActiveCamera();
        const viewAngle = camera.getViewAngle();
        const zoomScale = camera.getParallelProjection()
            ? 1
            : Math.tan((viewAngle * Math.PI) / 360) / Math.tan((DEFAULT_VIEW_ANGLE * Math.PI) / 360);

        // Drag right → RAO (oblique decreases), drag down → CRA (craniocaudal
        // increases) — matches the free RotateTool feel at the frontal view
        const deltaOblique = (-ROTATION_FACTOR * zoomScale * dx) / width;
        const deltaCraniocaudal = (ROTATION_FACTOR * zoomScale * dy) / height;

        // The service clamps; the camera binding reapplies the pose
        this.carmViewService.setDraftAngles({
            craniocaudal: draft.angles.craniocaudal + deltaCraniocaudal,
            oblique: draft.angles.oblique + deltaOblique,
        });
    }
}
